<template>
    <div ref="contactInput" class="contacts-input-div" @click="handleFocus" @focusout="handleBlur">
        <span
            v-if="!isInputVisible && emailAddresses.length <= 0"
            class="contacts-input-placeholder typographies-paragraph-medium-regular"
        >
            {{ localizedTexts.searchContacts }}
        </span>
        <CompassTagInteractive
            v-for="emailAddress in tagValues"
            :key="emailAddress.id"
            size="small"
            :color="getTagColor(emailAddress)"
            :modelValue="emailAddress.id"
            :valueLabel="findParticipant(emailAddress.id)"
            class="contacts-tags"
            @tag-remove="handleRemoveTag(emailAddress.id)"
        />
        <input
            v-show="isInputVisible"
            ref="contactsInput"
            v-model="input"
            maxlength="320"
            class="contacts-input typographies-paragraph-medium-regular"
            @input="onInput"
            @keydown.,.prevent="handleEmail"
            @keydown.tab.prevent="handleEmail"
            @keydown.enter.prevent="handleEmail"
            @focus="handleFocus"
        />
        <CompassDropdown
            ref="contactsDropdown"
            :noResultsLabel="localizedTexts.noResults"
            :value="valueFromAutoComplete"
            :isVisible="isDropdownVisible"
            :noResults="dataFromAutocomplete.length === 0"
            parentRef="contactInput"
            :loading="isDropdownLoading"
            dropdownClass="contacts-dropdown"
        >
            <CompassDropdownOption
                v-for="item in dataFromAutocomplete"
                :key="item.id"
                :label="`${item.first_name} ${item.last_name}`"
                :value="item.id"
                @itemSelected="onItemSelected(item.id)"
            >
                <CompassAvatar :initials="getInitials(item)" size="extra-small" />

                <span class="contacts-dropdown-item-text"
                    >{{ item.first_name }} {{ item.last_name }}</span
                >
            </CompassDropdownOption>
        </CompassDropdown>
    </div>
</template>
<script>
import { debounce, capitalize } from 'lodash';
import { hasWhiteSpace } from 'Helpers/stringHelper';
import ActivityFormMixins from 'mixins/activities/ActivityFormMixins.js';

export default {
    mixins: [ActivityFormMixins],
    props: {
        contacts: {
            type: Array,
            default: () => [],
        },
        dataFromAutocomplete: {
            type: Array,
            default: () => [],
        },
        isDropdownLoading: {
            type: Boolean,
            default: false,
        },
        embedParticipants: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            input: '',
            labelClass: [],
            itemHovered: 0,
            isDropdownVisible: false,
            isInputVisible: false,
            emailAddresses: this.contacts ?? [],
            contactsInformation: [],
        };
    },
    computed: {
        valueFromAutoComplete() {
            return this.contacts
                .filter((contact) => contact.type === 'contact')
                .map((contact) => contact.id);
        },
        tagValues() {
            return this.contacts.filter(
                (participant) =>
                    !participant.id.toString().includes('@resource.calendar.google.com'),
            );
        },
    },
    created() {
        this.contactsInformation = this.contacts
            .filter((participant) => participant.type === 'contact')
            .map((participant) => {
                let information = this.dataFromAutocomplete.find(
                    (contact) => contact.id === participant.id,
                );
                if (!information) {
                    information = this.embedParticipants.find(
                        (contact) => contact.id === participant.id,
                    );
                }
                return information;
            });
    },
    methods: {
        onItemSelected(value) {
            const contact = this.dataFromAutocomplete.find((item) => item.id === value);

            if (contact && !this.isEmailAdded(contact.email)) {
                this.addEmailAddress(contact);
            } else {
                this.handleRemoveTag(contact.email);
            }
            this.isDropdownVisible = false;
        },
        onInput: debounce(function () {
            this.$emit('input', this.input);
        }, 500),
        validateEmail(value) {
            const regex = /^((?!\.)[\w-_.]*[^.])(@\w+)(\.\w+(\.\w+)?[^.\W])$/gim;
            return !!regex.test(value.toLowerCase());
        },
        getTagColor(value) {
            if (value.type === 'external') {
                return this.validateEmail(value.id) ? 'blue' : 'red';
            }
            return 'blue';
        },
        handleEmail() {
            if (this.dataFromAutocomplete.length > 0) {
                this.onItemSelected(this.dataFromAutocomplete[this.itemHovered].id);
            } else if (this.input.trim().length && !hasWhiteSpace(this.input)) {
                this.addEmailAddress();
            }
        },
        addEmailAddress(contact = null) {
            if (this.emailAddresses.some((email) => email.id === contact?.id ?? this.input)) {
                this.handleRemoveTag(contact?.id ?? this.input);
                return;
            }

            this.emailAddresses.push({
                id: contact?.id ?? this.input,
                type: contact ? 'contact' : 'external',
            });

            if (contact) {
                this.contactsInformation.push(
                    this.dataFromAutocomplete.find((item) => item.id === contact.id),
                );
            }

            this.$emit('update:contacts', this.emailAddresses);
            this.$emit('input', '');
            this.input = '';
            this.checkInputHeight();
            this.$refs.contactsDropdown.updatePopper();
        },
        checkInputHeight() {
            this.$nextTick(() => {
                const contactInput = this.$refs.contactInput;
                this.labelClass = contactInput?.clientHeight > 32 ? ['alignStart'] : [''];
            });
        },
        isEmailAdded(email) {
            return this.emailAddresses.some((item) => item.id === email);
        },
        handleFocus() {
            this.isInputVisible = true;
            this.$nextTick(() => {
                this.$refs.contactsInput.focus();
            });
            this.$refs.contactsDropdown.updatePopper();
            this.isDropdownVisible = true;
        },
        handleBlur() {
            this.$refs.contactsInput.blur();
            this.isInputVisible = false;
            this.isDropdownVisible = false;
            this.input = '';
        },
        handleRemoveTag(emailAddress) {
            this.emailAddresses = this.emailAddresses.filter((email) => email.id !== emailAddress);
            this.$emit('update:contacts', this.emailAddresses);
            this.contactsInformation = this.contactsInformation.filter(
                (contact) => contact.id !== emailAddress,
            );
            this.checkInputHeight();
            this.$refs.contactsDropdown.updatePopper();
        },
        findParticipant(email) {
            const participant = this.contactsInformation.find((contact) => contact.id === email);
            if (participant) {
                return `${capitalize(participant.first_name)} ${capitalize(participant.last_name)}`;
            } else {
                return email;
            }
        },
        getInitials(person) {
            const initialFirstName = person.first_name ? person.first_name[0] : '';
            const initialLastName = person.last_name ? person.last_name[0] : '';
            return `${initialFirstName}${initialLastName}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.contacts-input-div {
    display: flex;
    align-items: center;
    flex-direction: row;
    flex-grow: 1;
    flex-wrap: wrap;
    gap: var(--spacing-0);
    border-radius: var(--spacing-0);
    border: 1px solid var(--colors-component-border-bloc-standard-default);
    background-color: var(--colors-component-background-bloc-standard-default);
    max-width: 254.73px;
    padding: var(--spacing-0);
    min-height: var(--spacing-5);
    max-height: 260px;
    overflow-y: auto;
    overflow-x: hidden;

    .contacts-tags {
        max-width: 100%;

        .contacts-tags-text {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .contacts-input {
        flex-grow: 1;
        min-width: 80px;
        border: none;
    }

    .contacts-input-placeholder {
        color: var(--colors-common-text-standard-placeholder);
        padding-left: 3px;
    }
}

#compass-dropdown-container {
    .contacts-dropdown {
        z-index: 1001;
        max-width: 256.73px;

        .contacts-dropdown-item-text {
            word-break: break-all;
        }
    }
}
</style>
